import { useEffect, useRef } from "react";
import * as d3 from "d3";

const CATEGORY_COLOR = {
  ingress: "#FF8A3D",
  compute: "#F2EDE6",
  iam: "#E8C14A",
  sensitive: "#FF3B30",
  other: "#5C5567",
};

const SENSITIVE_TYPES = new Set([
  "aws_s3_bucket",
  "aws_db_instance",
  "aws_rds_cluster",
  "aws_dynamodb_table",
  "aws_elasticache_cluster",
  "aws_elasticache_replication_group",
]);

const COMPUTE_TYPES = new Set([
  "aws_instance",
  "aws_lambda_function",
  "aws_ecs_service",
  "aws_ecs_task_definition",
  "aws_eks_cluster",
  "aws_autoscaling_group",
]);

function nodeCategory(type) {
  if (type.includes("security_group")) return "ingress";
  if (SENSITIVE_TYPES.has(type)) return "sensitive";
  if (type.startsWith("aws_iam_")) return "iam";
  if (COMPUTE_TYPES.has(type)) return "compute";
  return "other";
}

const CATEGORY_PHRASE = {
  ingress: (n) => `the internet-exposed security group \`${n.name}\``,
  sensitive: (n) => `sensitive resource \`${n.name}\` (${n.type})`,
  iam: (n) => `IAM resource \`${n.name}\``,
  compute: (n) => `compute resource \`${n.name}\``,
  other: (n) => `\`${n.name}\``,
};

function describeNode(n) {
  return CATEGORY_PHRASE[nodeCategory(n.type)](n);
}

function buildNarrative(path, nodeById) {
  const nodes = path.map((id) => nodeById.get(id)).filter(Boolean);
  if (nodes.length < 2) return "";
  const entry = nodes[0];
  const target = nodes[nodes.length - 1];
  const middle = nodes.slice(1, -1);
  const middleStr = middle.length
    ? ` by pivoting through ${middle.map((n) => `\`${n.name}\``).join(" → ")}`
    : "";
  return `An attacker entering through ${describeNode(entry)} could reach ${describeNode(target)}${middleStr}.`;
}

function PathList({ paths, nodeById }) {
  return (
    <div className="attack-path-list">
      {paths.map((path, i) => {
        const names = path.map((id) => nodeById.get(id)?.name || id);
        return (
          <div className="attack-path-item" key={i}>
            <div className="finding-top">
              <span className="sev-badge badge-CRITICAL">CRITICAL</span>
              <span className="finding-title">Path {i + 1} · {path.length - 1} hop{path.length - 1 === 1 ? "" : "s"} to sensitive data</span>
            </div>
            <div className="attack-path-narrative">{buildNarrative(path, nodeById)}</div>
            <div className="attack-path-chain">{names.join(" → ")}</div>
          </div>
        );
      })}
    </div>
  );
}

function drag(simulation) {
  function dragstarted(event, d) {
    if (!event.active) simulation.alphaTarget(0.25).restart();
    d.fx = d.x;
    d.fy = d.y;
  }
  function dragged(event, d) {
    d.fx = event.x;
    d.fy = event.y;
  }
  function dragended(event, d) {
    if (!event.active) simulation.alphaTarget(0);
    d.fx = null;
    d.fy = null;
  }
  return d3.drag().on("start", dragstarted).on("drag", dragged).on("end", dragended);
}

function GraphCanvas({ graphData }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!graphData?.nodes?.length) return;
    const width = containerRef.current?.clientWidth || 800;
    const height = 420;

    const pathEdgeKeys = new Set();
    const pathNodeIds = new Set();
    (graphData.attack_paths || []).forEach((path) => {
      path.forEach((id) => pathNodeIds.add(id));
      for (let i = 0; i < path.length - 1; i++) {
        pathEdgeKeys.add(`${path[i]}|${path[i + 1]}`);
        pathEdgeKeys.add(`${path[i + 1]}|${path[i]}`);
      }
    });

    const nodes = graphData.nodes.map((n) => ({ ...n }));
    const nodeIds = new Set(nodes.map((n) => n.id));
    const links = (graphData.edges || [])
      .filter((e) => nodeIds.has(e.from) && nodeIds.has(e.to))
      .map((e) => ({ source: e.from, target: e.to, onPath: pathEdgeKeys.has(`${e.from}|${e.to}`) }));

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("viewBox", [0, 0, width, height]).attr("width", "100%").attr("height", height);

    const defs = svg.append("defs");
    defs
      .append("marker")
      .attr("id", "ts-arrow")
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", 21)
      .attr("refY", 0)
      .attr("markerWidth", 6)
      .attr("markerHeight", 6)
      .attr("orient", "auto")
      .append("path")
      .attr("d", "M0,-5L10,0L0,5")
      .attr("fill", "rgba(143,135,153,0.55)");
    defs
      .append("marker")
      .attr("id", "ts-arrow-path")
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", 21)
      .attr("refY", 0)
      .attr("markerWidth", 7)
      .attr("markerHeight", 7)
      .attr("orient", "auto")
      .append("path")
      .attr("d", "M0,-5L10,0L0,5")
      .attr("fill", "#FF3B30");

    const g = svg.append("g");
    svg.call(
      d3.zoom()
        .scaleExtent([0.4, 2.5])
        .on("zoom", (event) => g.attr("transform", event.transform))
    );

    const simulation = d3
      .forceSimulation(nodes)
      .force("link", d3.forceLink(links).id((d) => d.id).distance(110).strength(0.6))
      .force("charge", d3.forceManyBody().strength(-320))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force("collide", d3.forceCollide(42));

    const link = g
      .append("g")
      .selectAll("line")
      .data(links)
      .join("line")
      .attr("stroke", (d) => (d.onPath ? "#FF3B30" : "rgba(143,135,153,0.3)"))
      .attr("stroke-width", (d) => (d.onPath ? 2.5 : 1.2))
      .attr("marker-end", (d) => (d.onPath ? "url(#ts-arrow-path)" : "url(#ts-arrow)"));

    const node = g
      .append("g")
      .selectAll("g")
      .data(nodes)
      .join("g")
      .attr("cursor", "grab")
      .call(drag(simulation));

    node
      .append("circle")
      .attr("r", (d) => (pathNodeIds.has(d.id) ? 16 : 12))
      .attr("fill", (d) => `${CATEGORY_COLOR[nodeCategory(d.type)]}22`)
      .attr("stroke", (d) => CATEGORY_COLOR[nodeCategory(d.type)])
      .attr("stroke-width", (d) => (pathNodeIds.has(d.id) ? 2.5 : 1.5));

    node
      .append("text")
      .text((d) => d.name)
      .attr("text-anchor", "middle")
      .attr("y", (d) => (pathNodeIds.has(d.id) ? 16 : 12) + 14)
      .attr("font-family", "JetBrains Mono, monospace")
      .attr("font-size", "9.5px")
      .attr("fill", (d) => (pathNodeIds.has(d.id) ? "#F2EDE6" : "#8F8799"));

    node.append("title").text((d) => d.id);

    const pad = 30;
    simulation.on("tick", () => {
      nodes.forEach((d) => {
        d.x = Math.max(pad, Math.min(width - pad, d.x));
        d.y = Math.max(pad, Math.min(height - pad, d.y));
      });
      link
        .attr("x1", (d) => d.source.x)
        .attr("y1", (d) => d.source.y)
        .attr("x2", (d) => d.target.x)
        .attr("y2", (d) => d.target.y);
      node.attr("transform", (d) => `translate(${d.x},${d.y})`);
    });

    return () => simulation.stop();
  }, [graphData]);

  return (
    <div className="attack-graph-canvas" ref={containerRef}>
      <svg ref={svgRef} />
    </div>
  );
}

export default function AttackGraph({ graphData, loading, error }) {
  const hasNodes = (graphData?.nodes?.length || 0) > 0;
  const pathCount = graphData?.critical_path_count || 0;
  const nodeById = new Map((graphData?.nodes || []).map((n) => [n.id, n]));
  const paths = graphData?.attack_paths || [];

  return (
    <div className="findings-panel attack-graph-panel">
      <div className="panel-header">
        <span className="panel-title">Attack Path Analysis</span>
        {hasNodes && (
          <span className={`attack-path-badge ${pathCount > 0 ? "danger" : "safe"}`}>
            {pathCount > 0 ? `${pathCount} attack path${pathCount > 1 ? "s" : ""} found` : "No path to sensitive data"}
          </span>
        )}
      </div>
      {loading && <div className="attack-graph-empty">Tracing resource relationships…</div>}
      {!loading && error && <div className="attack-graph-empty">Attack graph unavailable</div>}
      {!loading && !error && !hasNodes && (
        <div className="attack-graph-empty">No resource references found to graph</div>
      )}
      {!loading && !error && hasNodes && (
        <>
          {pathCount === 0 && (
            <div className="attack-graph-empty" style={{ padding: "1.25rem", textAlign: "left" }}>
              No route from a public-facing ingress rule to a sensitive data store (S3, RDS, DynamoDB, ElastiCache) was found among these resources' cross-references.
            </div>
          )}
          {pathCount > 0 && <PathList paths={paths} nodeById={nodeById} />}
          <GraphCanvas graphData={graphData} />
          <div className="attack-graph-legend">
            <span><i style={{ background: CATEGORY_COLOR.ingress }} />Public entry</span>
            <span><i style={{ background: CATEGORY_COLOR.compute }} />Compute</span>
            <span><i style={{ background: CATEGORY_COLOR.iam }} />IAM</span>
            <span><i style={{ background: CATEGORY_COLOR.sensitive }} />Sensitive data</span>
            <span className="legend-path"><i />Attack path</span>
          </div>
        </>
      )}
    </div>
  );
}
